import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { Box, Typography, Alert, Button, Modal } from "@mui/material";
import axios from "axios";
import { baseURL } from "../Constant";
import "./MyDesignsTable.css";

const MyDesignsTable = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const loggedInEnterpriseId =
        activeAccount?.idTokenClaims?.enterpriseID || "";

    const [designs, setDesigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openFilesModal, setOpenFilesModal] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    const handleOpenFiles = (files) => {
        const validFiles = files?.filter(f => f.file_name) || [];
        setSelectedFiles(validFiles);
        setOpenFilesModal(true);
    };

    const handleCloseFiles = () => {
        setOpenFilesModal(false);
        setSelectedFiles([]);
    };

    const handleDownload = (filePath, fileName) => {
        const url = `${baseURL}/download-file?filePath=${encodeURIComponent(filePath)}`;

        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    useEffect(() => {
        if (!loggedInEnterpriseId) return;

        fetchMyDesigns();
    }, [loggedInEnterpriseId]);

    const fetchMyDesigns = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${baseURL}/my-designs/${loggedInEnterpriseId}`
            );

            setDesigns(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            field: "id",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>SI#</div>
            ),
            renderCell: (params) =>
                params.api.getSortedRowIds().indexOf(params.id) + 1,
        },
        {
            field: "program_name",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Program Name</div>
            ),
            flex: 1.5,
        },
        {
            field: "market",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Market</div>
            ),
        },
        {
            field: "collateral_type",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Collateral Type</div>
            ),
            flex: 1.2,
        },
        {
            field: "no_of_collaterals",
            headerName: (
                <div style={{ paddingLeft: "10px" }}># Collaterals</div>
            ),
        },
        {
            field: "start_date",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Start Date</div>
            ),
            renderCell: (params) =>
                params.row.start_date
                    ? new Date(params.row.start_date).toLocaleDateString("en-GB")
                    : "—",
        },
        {
            field: "end_date",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>End Date</div>
            ),
            renderCell: (params) =>
                params.row.end_date
                    ? new Date(params.row.end_date).toLocaleDateString("en-GB")
                    : "—",
        },
        {
            field: "assigned_by",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Assigned By</div>
            ),
        },
        {
            field: "files",
            headerName: (
                <div style={{ paddingLeft: "10px" }}>Files</div>
            ),
            renderCell: (params) => {
                const files = params.row.files?.filter(f => f.file_name) || [];

                return (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={files.length === 0}
                        onClick={() => handleOpenFiles(files)}
                        sx={{ textTransform: "none" }}
                    >
                        {files.length > 0 ? `${files.length} Files` : "No Files"}
                    </Button>
                );
            }
        }
    ];

    return (
        <>
            {loading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Loading your designs...
                </Alert>
            )}

            {!loading && designs.length === 0 ? (
                <Box sx={{ textAlign: "center", marginTop: "100px" }}>
                    <Typography variant="h6">
                        No designs assigned to you yet
                    </Typography>
                </Box>
            ) : (
                <div
                    style={{
                        height: 450,
                        width: "100%",
                        overflowX: "auto"
                    }}
                >
                    <DataGrid
                        rows={designs}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        disableSelectionOnClick
                        disableColumnMenu
                        sortingOrder={["asc", "desc"]}
                        sx={{
                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: "#022278",
                                color: "white",
                                padding: "0px !important",
                            },
                            "& .MuiDataGrid-columnHeaderTitleContainer": {
                                textAlign: "left",
                            },
                            "& .MuiDataGrid-root": {
                                width: "100%",
                            },
                            "& .MuiDataGrid-sortIcon": {
                                color: "#fff",
                            },

                            // ✅ THIS FIXES HEADER WIDTH ISSUE
                            "& .MuiDataGrid-columnHeaders": {
                                width: "var(--DataGrid-rowWidth)",
                            },

                            "& .MuiDataGrid-row": {
                                backgroundColor: "white",
                                width: "calc(100% - 2px)",
                            },
                        }}
                        slots={{ toolbar: GridToolbarQuickFilter }}
                    />
                </div>
            )}

            <Modal open={openFilesModal} onClose={handleCloseFiles}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "400px",
                        bgcolor: "white",
                        borderRadius: 3,
                        boxShadow: 24,
                        p: 3,
                    }}
                >
                    <Typography variant="h6" mb={2}>
                        Attached Files
                    </Typography>

                    {selectedFiles.length === 0 ? (
                        <Typography color="text.secondary">
                            No files available
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                            }}
                        >
                            {selectedFiles.map((file) => (
                                <Box
                                    key={file.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        border: "1px solid #eee",
                                        borderRadius: "10px",
                                        p: 1.2,
                                        "&:hover": {
                                            backgroundColor: "#f9f9f9",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            maxWidth: "220px",
                                        }}
                                    >
                                        {file.file_name}
                                    </Typography>

                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() =>
                                            handleDownload(file.file_path, file.file_name)
                                        }
                                        sx={{
                                            textTransform: "none",
                                            fontSize: "12px",
                                        }}
                                    >
                                        Download
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box mt={2} textAlign="right">
                        <Button onClick={handleCloseFiles}>Close</Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default MyDesignsTable;